import { useCallback, useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { getDocumentTypesDict } from '@Store/selectors/dictionarySelector';
import { transformDictToOptions } from '@Utils/transformToOptions';
import { currentMaterialRequest } from '@Features/MaterialRequest/requests/MaterialRequisition/store/selectors/materialRequestSelector';
import { useGetRequisitionDocumentsMutation } from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';

import { MaterialRequisitionDocumentRequest } from '@Types/api/form';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { RcFile } from 'antd/es/upload';
import { getDocument } from '@Features/utils/document';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import { Attachment } from '@Components/types/Attachment';

import { currentUserSelector } from '@Store/selectors/userInfoSelector';

import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { generateFilepath } from '@Utils/generateFilepath';
import ModalContent from './components/ModalContent';
import { getDocumentFolder } from '../../../../utils/document';

interface EditModalContentProps {
  itemId: string | number;

  formik: FormikProps<MaterialRequisitionDocumentRequest>;
}

const EditModalContent: React.FC<EditModalContentProps> = ({
  itemId,
  formik,
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const currentMRequest = useAppSelector(currentMaterialRequest);
  const documentTypes = useAppSelector(getDocumentTypesDict);
  const [getRequisitionDocuments] = useGetRequisitionDocumentsMutation();
  const [getFormList] = useFormListMutation();
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );

  const onFetchDocumentFile = useCallback(
    (requestDocumentId: string | number, fileName: string) => {
      setUploadedFile(undefined);
      dispatch(fileUploadActions.setFile(null));

      if (!requestDocumentId || !fileName) {
        return;
      }

      const documentFolderPath = getDocumentFolder(requestDocumentId);

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          const matchedDocument = getDocument(response, fileName);
          if (matchedDocument) {
            setUploadedFile({
              id: matchedDocument.documentId,
              name: fileName,
            });
          }
        });
    },
    []
  );

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'MR',
          (file as RcFile).name,
          currentUser?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [currentUser]
  );

  useEffect(() => {
    if (currentMRequest?.requestId) {
      getRequisitionDocuments(String(currentMRequest?.requestId))
        .unwrap()
        .then((response) => {
          const findedDocument = response.find(
            (document) => document.filePath === itemId
          );

          if (findedDocument) {
            onFetchDocumentFile(findedDocument.reqId!, findedDocument.filePath);

            formik.setValues({
              ...findedDocument,
              reqId: Number(findedDocument.reqId),
            });
          }
        });
    }
  }, [currentMRequest?.requestId]);

  return (
    <ModalContent
      formik={formik}
      documentTypes={transformDictToOptions(documentTypes)}
      handleSubmit={formik.handleSubmit}
      onFileRemove={() => null}
      onFileRequest={handleFileRequest}
      uploadedFile={uploadedFile}
    />
  );
};

export default EditModalContent;
